import React, { useEffect, memo, useState, useRef } from 'react';
import './index.less';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { IInitInfo } from '../..';
import { getPredictScoreExamPointChart } from '@/apis';
import queryDomMethods from '@/utils/queryDomMethods';
import { IPredictionScoreExamPointChart } from '@/types';

echarts.use([TooltipComponent, GridComponent, BarChart, CanvasRenderer]);

interface IProps {
    initInfo?: IInitInfo;
}

const ExamPointChart = (props: IProps) => {
    const { initInfo } = props;
    const myChart = useRef<echarts.EChartsType>();
    const [chartData, setChartData] = useState<IPredictionScoreExamPointChart>({
        difficultList: [],
        maxDifficultQuestionNum: 0, // 考点下最大难度题目数
        predictionRightNum: 0, // 考点下预计会作对的题目数
        totalQuestionNum: 0, // 考点下总题目数
    });

    const initChart = (xAxisData: string[], totalData: number[], predictionRightData: number[], yAxisMinInterval: number) => {
        if (myChart.current) {
            myChart.current.dispose();
            myChart.current = undefined;
        }

        const chartDom = queryDomMethods.querySelector('#prediction-score-chart-main');
        myChart.current = echarts.init(chartDom);

        const option = {
            xAxis: {
                type: 'category',
                data: xAxisData,
                axisLabel: {
                    color: '#666666',
                },
                axisLine: {
                    lineStyle: {
                        color: '#F0F0F0',
                    },
                },
                axisTick: {
                    show: false,
                },
            },
            yAxis: {
                type: 'value',
                name: '数量/道',
                startValue: 0,
                minInterval: yAxisMinInterval, // 最小间隔大小
                nameTextStyle: {
                    color: '#999999',
                    align: 'right',
                },
                axisLabel: {
                    color: '#999999',
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#F0F0F0',
                        width: 0.5,
                        type: 'dashed',
                    },
                },
            },
            grid: {
                show: false,
                left: 44, // 调整图表距离左侧的距离
                right: 0, // 调整图表距离右侧的距离
                top: 34, // 调整图表距离顶部的距离
                bottom: 20, // 调整图表距离底部的距离
            },
            tooltip: {
                show: false,
            },
            series: [
                {
                    name: '总数',
                    data: totalData,
                    type: 'bar',
                    barWidth: '20px',
                    itemStyle: {
                        color: '#F5F5F5',
                        borderRadius: [5, 5, 0, 0],
                    },
                    barGap: '-100%', // 100%重叠
                    emphasis: {
                        disabled: true,
                    },
                    label: {
                        show: true,
                        position: 'top',
                        formatter: (params: any) => {
                            return params.value ? ((predictionRightData[params.dataIndex] / params.value) * 100).toFixed(0) + '%' : '0%';
                        },
                        color: '#333333',
                        align: 'center',
                        lineHeight: 18,
                    },
                },
                {
                    name: '预测做对',
                    data: predictionRightData,
                    type: 'bar',
                    barWidth: '20px',
                    z: 2,
                    itemStyle: {
                        color: '#03C785',
                    },
                    barGap: '-100%', // 100% 重叠
                    emphasis: {
                        disabled: true,
                    },
                },
            ],
        };

        option && myChart.current.setOption(option);
    };

    const fetchChart = async () => {
        const response = await getPredictScoreExamPointChart({
            projectId: initInfo!.projectId,
            subjectId: initInfo!.subjectId,
            examSeasonId: initInfo!.examSeasonId,
            knowledgeId: initInfo!.knowledgeId,
        });
        if (response.status === 0 && response.result) {
            setChartData(response.result);
            const { difficultList, maxDifficultQuestionNum } = response.result;
            const xAxisData: string[] = [];
            const totalData: number[] = [];
            const predictionRightData: number[] = [];
            difficultList.forEach(({ name, predictionRightNum, totalQuestionNum }) => {
                xAxisData.push(name);
                totalData.push(totalQuestionNum);
                predictionRightData.push(predictionRightNum);
            });
            const yAxisMinInterval = maxDifficultQuestionNum ? Math.ceil(maxDifficultQuestionNum / 5 / 5) * 5 : 10;
            initChart(xAxisData, totalData, predictionRightData, yAxisMinInterval);
        }
    };

    useEffect(() => {
        initInfo?.hasAllRequiredParams && fetchChart();
    }, [initInfo?.knowledgeId]);

    useEffect(
        () => () => {
            if (myChart.current) {
                myChart.current.dispose();
                myChart.current = undefined;
            }
        },
        []
    );

    return (
        <div className="sub-tiku__exam-point-chart">
            <div className="sub-tiku__exam-point-chart-legend">
                <div>
                    <i style={{ background: '#EEEEEE' }} /> 考点总题数：{chartData.totalQuestionNum}道
                </div>
                <div>
                    <i style={{ background: '#03C785' }} /> 预测做对数：{chartData.predictionRightNum}道
                </div>
            </div>
            <div className="sub-tiku__exam-point-chart-header">
                <span>考点预测掌握情况</span>
            </div>
            <div className="sub-tiku__exam-point-chart-content">
                <div id="prediction-score-chart-main"></div>
            </div>
        </div>
    );
};

export default memo(ExamPointChart);
